/**
 * @file Array Editor for schema-based array editing (list mode)
 * @version 3.0.0
 */

import BaseEditor from './BaseEditor.js';
import { CONTROLS_TEMPLATE } from './PrimitiveEditor.js';
import PaginationControls from './PaginationControls.js';
import FilterBar from './FilterBar.js';
import { createDefaultDocument } from './schemas.js';
import { compareDocuments, matchesSelector } from './applyQuery.js';
import { registerEditor } from './component-registry.js';

export const ARRAY_ITEM_CONTROLS_TEMPLATE = `
  <template v-if="showArrayControls">
    <template v-if="isMovable">
      <button
        @click="moveItemUp(displayIndex)"
        :disabled="!canMoveUp(displayIndex)"
        class="btn btn-outline-secondary btn-sm"
        :title="moveUpTitle"
      >
        <i class="bi" :class="moveUpIcon"></i>
      </button>

      <button
        @click="moveItemDown(displayIndex)"
        :disabled="!canMoveDown(displayIndex)"
        class="btn btn-outline-secondary btn-sm"
        :title="moveDownTitle"
      >
        <i class="bi" :class="moveDownIcon"></i>
      </button>
    </template>

    <button
      @click="removeItem(displayIndex)"
      class="btn btn-danger btn-sm"
      title="Delete item"
    >
      <i class="bi bi-trash"></i>
    </button>
  </template>
`;

export const ARRAY_HEADER_TEMPLATE = `
  <!-- Header with title and controls -->
  <div class="d-flex align-items-baseline gap-2 mb-2" :style="controlStyles">
    <h6 v-if="getAttribute('displayLabel', true)" class="mb-0 fw-bold">
      {{ editorTitle }}
      <span v-if="isUndefined" class="text-muted fst-italic fw-normal ms-2">—</span>
      <span v-else-if="isEmpty" class="text-muted fst-italic fw-normal ms-2">(empty)</span>
      <span v-else-if="hasActiveFilter" class="text-muted fst-italic fw-normal ms-2">({{ filteredResult.array.length }} / {{ itemsOrder.length }} items)</span>
      <span v-else class="text-muted fst-italic fw-normal ms-2">({{ itemsOrder.length }} items)</span>
    </h6>
    <div v-if="!getAttribute('displayLabel', true) || !editorTitle"></div>

    <div
      v-if="hasErrors && (mode === 'edit' || (mode === 'click-edit' && isEditing))"
      class="invalid-feedback d-block mt-1"
    >
      {{ validationError }}
    </div>

    <!-- Filter bar -->
    <filter-bar
      v-if="getAttribute('useFilterBar', useFilterBarDefault) && filterActive && isFilterSupported && !isUndefined"
      :schema="getItemSchema()"
      :query="effectiveQuery"
      @update:query="handleQueryChange($event)"
      class="me-auto"
    />

    <!-- spacer -->
    <div class="me-auto"></div>

    <button
      v-if="canShowFilterToggle && !isUndefined"
      @click="toggleFilter()"
      class="btn btn-sm"
      :class="filterActive ? 'btn-primary' : 'btn-outline-secondary'"
      title="Toggle filter"
    >
      <i class="bi bi-funnel"></i>
    </button>

    <div class="d-flex gap-1 align-items-center">
      <!-- Add Item button (array-specific, before standard controls) -->
      <button
        v-if="showArrayControls"
        @click="addItem()"
        class="btn btn-outline-primary btn-sm"
      >
        <i class="bi bi-plus"></i>
      </button>

      ${CONTROLS_TEMPLATE}
    </div>
  </div>
`;

export const ArrayEditor = {
  name: 'ArrayEditor',
  extends: BaseEditor,

  setup(props, ctx) {
    return BaseEditor.setup(props, ctx);
  },

  components: {
    'pagination-controls': PaginationControls,
    'filter-bar': FilterBar,
  },

  props: {
    schema: {
      type: Object,
      required: true,
      validator(schema) {
        return schema && schema.type === 'array' && schema.items;
      },
    },
    value: {
      type: Array,
    },
    reference: {
      type: Array,
    },
    pageSize: {
      type: Number,
      default: 20,
      validator: v => v > 0,
    },
    showPagination: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: Array,
      default: () => [],
    },
    query: {
      type: Object,
      default: () => ({ selector: {} }),
    },
    showFilter: {
      type: Boolean,
    },
    useFilterBar: {
      type: Boolean,
    },
    useFilterHeader: {
      type: Boolean,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'input',
    'update:value',
    'page-change',
    'update:sort',
    'update:query',
  ],

  data() {
    return {
      itemsOrder: [],
      currentPage: 1, // 1-indexed
      internalSort: [], // For uncontrolled mode
      internalQuery: { selector: {} }, // For uncontrolled mode
      filterActive: false, // Toggle state for filter bar
      useFilterHeaderDefault: false,
      useFilterBarDefault: true,
    };
  },

  computed: {
    /**
     * Check if array is empty
     */
    isEmpty() {
      return !this.isUndefined && this.itemsOrder.length === 0;
    },

    /**
     * Add label for array
     */
    addLabel() {
      return 'Add Array';
    },

    /**
     * Move up icon based on orientation
     */
    moveUpIcon() {
      return this.vertical ? 'bi-chevron-left' : 'bi-chevron-up';
    },

    /**
     * Move down icon based on orientation
     */
    moveDownIcon() {
      return this.vertical ? 'bi-chevron-right' : 'bi-chevron-down';
    },

    /**
     * Move up title based on orientation
     */
    moveUpTitle() {
      return this.vertical ? 'Move left' : 'Move up';
    },

    /**
     * Move down title based on orientation
     */
    moveDownTitle() {
      return this.vertical ? 'Move right' : 'Move down';
    },

    /**
     * Effective sort specification (from prop or internal state)
     */
    effectiveSort() {
      return this.sort.length > 0 ? this.sort : this.internalSort;
    },

    /**
     * Check if filter is supported (only for object items)
     */
    isFilterSupported() {
      const itemSchema = this.getItemSchema();
      return itemSchema?.type === 'object';
    },

    /**
     * Whether to show the filter toggle button
     */
    canShowFilterToggle() {
      return (
        this.getAttribute('showFilter', true) &&
        this.isFilterSupported &&
        (this.getAttribute('useFilterBar', this.useFilterBarDefault) ||
          this.getAttribute('useFilterHeader', this.useFilterHeaderDefault))
      );
    },

    /**
     * Effective query (from prop or internal state)
     */
    effectiveQuery() {
      const propHasSelector =
        this.query?.selector && Object.keys(this.query.selector).length > 0;
      return propHasSelector ? this.query : this.internalQuery;
    },

    /**
     * Whether filtering is currently active (toggle on AND has selector)
     */
    hasActiveFilter() {
      if (!this.filterActive) return false;
      return (
        this.effectiveQuery?.selector &&
        Object.keys(this.effectiveQuery.selector).length > 0
      );
    },

    /**
     * Step 1: Ordered array with index tracking
     */
    orderedResult() {
      if (!this.internalValue.editable) {
        return { array: [], indices: [] };
      }
      return {
        array: this.itemsOrder.map(i => this.internalValue.editable[i]),
        indices: [...this.itemsOrder],
      };
    },

    /**
     * Step 2: Filtered result with index tracking
     */
    filteredResult() {
      const { array, indices } = this.orderedResult;

      // Skip filtering if toggle is off
      if (!this.filterActive) {
        return { array, indices };
      }

      const selector = this.effectiveQuery?.selector;
      if (!selector || Object.keys(selector).length === 0) {
        return { array, indices };
      }

      const filtered = [];
      const filteredIndices = [];

      for (let i = 0; i < array.length; i++) {
        if (matchesSelector(array[i], selector)) {
          filtered.push(array[i]);
          filteredIndices.push(indices[i]);
        }
      }

      return { array: filtered, indices: filteredIndices };
    },

    /**
     * Step 3: Sorted result with index tracking
     */
    sortedResult() {
      const { array, indices } = this.filteredResult;

      if (!this.effectiveSort.length) {
        return { array, indices };
      }

      const paired = array.map((item, i) => ({
        item,
        originalIndex: indices[i],
      }));

      paired.sort((a, b) =>
        compareDocuments(a.item, b.item, this.effectiveSort)
      );

      return {
        array: paired.map(p => p.item),
        indices: paired.map(p => p.originalIndex),
      };
    },

    /**
     * Total number of pages
     */
    totalPages() {
      if (!this.showPagination) return 1;
      return Math.max(
        1,
        Math.ceil(this.sortedResult.array.length / this.pageSize)
      );
    },

    /**
     * Total items count (after sorting, before pagination)
     */
    totalItems() {
      return this.sortedResult.array.length;
    },

    /**
     * Step 4: Paginated result with index tracking
     */
    paginatedResult() {
      const { array, indices } = this.sortedResult;

      if (!this.showPagination) {
        return { array, indices };
      }

      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;

      return {
        array: array.slice(start, end),
        indices: indices.slice(start, end),
      };
    },

    /**
     * Final display array (used in template)
     */
    displayArray() {
      return this.paginatedResult.array;
    },

    /**
     * Display indices mapping to source indices
     */
    displayIndices() {
      return this.paginatedResult.indices;
    },

    /**
     * Whether pagination controls should be visible
     */
    isPaginationVisible() {
      return this.showPagination && this.totalPages > 1;
    },

    /**
     * Whether items can be manually reordered
     * (disabled when sorting is active)
     */
    isMovable() {
      return (
        this.getAttribute('movable', true) && this.effectiveSort.length === 0
      );
    },

    /**
     * Whether sorting is enabled (via sortable attribute)
     */
    isSortable() {
      return this.getAttribute('sortable', false);
    },

    /**
     * Check if array controls should show
     */
    showArrayControls() {
      return (
        this.canEdit &&
        (this.mode === 'edit' ||
          this.mode === 'field-click-edit' ||
          this.mode === 'form' ||
          (this.mode === 'click-edit' && this.isEditing))
      );
    },

    /**
     * Check if order has changed
     */
    hasOrderChanges() {
      const originalOrder = this.value
        ? this.value.map((_, index) => index)
        : [];
      return JSON.stringify(this.itemsOrder) !== JSON.stringify(originalOrder);
    },

    /**
     * Override hasChanges to include order
     */
    hasChanges() {
      return this.internalValue.isModified || this.hasOrderChanges;
    },

    /**
     * External state
     */
    externalState() {
      const state = {};

      if (this.hasErrors) {
        state.state = 'error';
      } else if (this.hasChanges) {
        state.state = 'updated';
        state.update = () =>
          this.isUndefined
            ? undefined
            : this.itemsOrder.map(
                dataIndex => this.internalValue.editable[dataIndex]
              );
      } else {
        state.state = 'no_change';
      }

      Object.defineProperty(state, 'sender', {
        value: this,
        enumerable: false,
        writable: false,
      });

      return state;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.itemsOrder = newValue ? newValue.map((_, index) => index) : [];
        this.childStates.clear();
        this.currentPage = 1; // Reset to first page on value change
      },
    },
    totalPages(newTotal) {
      // Adjust page when items removed
      if (this.currentPage > newTotal) {
        this.currentPage = Math.max(1, newTotal);
      }
    },
  },

  methods: {
    /**
     * Get source index from display index
     * @param {number} displayIndex - Index in the displayed (paginated) array
     * @returns {number} Index in the source array
     */
    getSourceIndex(displayIndex) {
      return this.displayIndices[displayIndex];
    },

    /**
     * Override reset to include itemsOrder
     */
    reset() {
      this.isEditing = false;
      this.internalValue.reset();
      this.itemsOrder = this.value ? this.value.map((_, index) => index) : [];
      this.childStates.forEach(state => state.sender?.reset?.());
      this.childStates.clear();
    },

    /**
     * Add array when undefined
     */
    addValue() {
      this.internalValue.editable = [];
      this.itemsOrder = [];
    },

    /**
     * Add new item
     */
    addItem() {
      const itemSchema = this.getItemSchema();
      const defaultValue = createDefaultDocument(itemSchema, this.getAttribute);
      this.internalValue.editable.push(defaultValue);
      this.itemsOrder.push(this.internalValue.editable.length - 1);
    },

    /**
     * Remove item
     * @param {number} displayIndex - Index in the displayed (paginated) array
     */
    removeItem(displayIndex) {
      const sourceIndex = this.getSourceIndex(displayIndex);
      // Find and remove from itemsOrder
      const orderIndex = this.itemsOrder.indexOf(sourceIndex);
      if (orderIndex !== -1) {
        this.itemsOrder.splice(orderIndex, 1);
      }
      this.childStates.delete(sourceIndex);
    },

    /**
     * Move item up in itemsOrder
     * @param {number} displayIndex - Index in the displayed (paginated) array
     */
    moveItemUp(displayIndex) {
      const sourceIndex = this.getSourceIndex(displayIndex);
      const orderIndex = this.itemsOrder.indexOf(sourceIndex);
      if (orderIndex > 0) {
        const [movedIndex] = this.itemsOrder.splice(orderIndex, 1);
        this.itemsOrder.splice(orderIndex - 1, 0, movedIndex);
      }
    },

    /**
     * Move item down in itemsOrder
     * @param {number} displayIndex - Index in the displayed (paginated) array
     */
    moveItemDown(displayIndex) {
      const sourceIndex = this.getSourceIndex(displayIndex);
      const orderIndex = this.itemsOrder.indexOf(sourceIndex);
      if (orderIndex < this.itemsOrder.length - 1) {
        const [movedIndex] = this.itemsOrder.splice(orderIndex, 1);
        this.itemsOrder.splice(orderIndex + 1, 0, movedIndex);
      }
    },

    /**
     * Check if item can move up (is not first in itemsOrder)
     * @param {number} displayIndex - Index in the displayed (paginated) array
     */
    canMoveUp(displayIndex) {
      const sourceIndex = this.getSourceIndex(displayIndex);
      const orderIndex = this.itemsOrder.indexOf(sourceIndex);
      return orderIndex > 0;
    },

    /**
     * Check if item can move down (is not last in itemsOrder)
     * @param {number} displayIndex - Index in the displayed (paginated) array
     */
    canMoveDown(displayIndex) {
      const sourceIndex = this.getSourceIndex(displayIndex);
      const orderIndex = this.itemsOrder.indexOf(sourceIndex);
      return orderIndex < this.itemsOrder.length - 1;
    },

    /**
     * Get item value
     * @param {number} displayIndex - Index in the displayed (paginated) array
     */
    getItemValue(displayIndex) {
      const sourceIndex = this.getSourceIndex(displayIndex);
      return this.internalValue.editable[sourceIndex];
    },

    /**
     * Get item reference using source index
     * @param {number} displayIndex - Index in the displayed (paginated) array
     */
    getItemReference(displayIndex) {
      if (this.reference === undefined) return undefined;
      const sourceIndex = this.getSourceIndex(displayIndex);
      return this.reference?.[sourceIndex] ?? null;
    },

    /**
     * Get item schema
     */
    getItemSchema() {
      return this.customizedSchema.items || {};
    },

    /**
     * Handle item input
     * @param {number} displayIndex - Index in the displayed (paginated) array
     * @param {object} itemState - State from child editor
     */
    handleItemInput(displayIndex, itemState) {
      const sourceIndex = this.getSourceIndex(displayIndex);
      this.childStates.delete(sourceIndex);
      if (itemState.state === 'updated' && itemState.update) {
        const doc = this.internalValue.editable;
        doc[sourceIndex] = itemState.update(doc[sourceIndex]);
      }
      if (itemState.state === 'error') {
        this.childStates.set(sourceIndex, itemState);
      }
    },

    /**
     * Override handleDelete
     */
    handleDelete() {
      this.internalValue.editable = undefined;
      this.itemsOrder = [];
    },

    /**
     * Handle page change from pagination controls
     * @param {number} page - New page number (1-indexed)
     */
    handlePageChange(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.$emit('page-change', {
          page: this.currentPage,
          pageSize: this.pageSize,
          totalPages: this.totalPages,
        });
      }
    },

    /**
     * Handle sort change from sort control
     * @param {Array} newSort - New sort specification
     */
    handleSortChange(newSort) {
      if (this.sort.length > 0) {
        // Controlled mode: emit update
        this.$emit('update:sort', newSort);
      } else {
        // Uncontrolled mode: update internal state
        this.internalSort = newSort;
      }
      // Reset to first page when sort changes
      this.currentPage = 1;
    },

    /**
     * Handle query change from filter bar
     * @param {object} newQuery - New query object with selector
     */
    handleQueryChange(newQuery) {
      const propHasSelector =
        this.query?.selector && Object.keys(this.query.selector).length > 0;
      if (propHasSelector) {
        // Controlled mode: emit update
        this.$emit('update:query', newQuery);
      } else {
        // Uncontrolled mode: update internal state
        this.internalQuery = newQuery;
      }
      // Reset to first page when filter changes
      this.currentPage = 1;
    },

    /**
     * Toggle filter active state
     */
    toggleFilter() {
      this.filterActive = !this.filterActive;
      // Reset to first page when toggling filter
      if (this.filterActive) {
        this.currentPage = 1;
      }
    },
  },

  template: `
    <div class="array-editor" v-if="canRead" v-bind="$attrs">
      ${ARRAY_HEADER_TEMPLATE}

      <!-- Items content -->
      <div v-if="!isUndefined" class="array-items">
        <div class="d-flex flex-column gap-2">
          <div
            v-for="(sourceIndex, displayIndex) in displayIndices"
            :key="sourceIndex"
            class="array-item"
          >
            <div class="border-start border-2 ps-3" style="border-color: #dee2e6 !important;">
              <component
                :is="getItemComponent(getItemSchema())"
                :schema="getItemSchema()"
                :value="getItemValue(displayIndex)"
                :reference="getItemReference(displayIndex)"
                :mode="childMode"
                :deletable="false"
                :displayLabel="false"
                @input="(state) => handleItemInput(displayIndex, state)"
              >
                <template #controls>
                  <div class="d-flex gap-1 align-items-center">
                    ${ARRAY_ITEM_CONTROLS_TEMPLATE}
                  </div>
                </template>
              </component>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="isEmpty" class="text-muted fst-italic py-1">
          Empty
        </div>

        <!-- Pagination controls -->
        <pagination-controls v-if="isPaginationVisible" />
      </div>
    </div>
  `,
};

registerEditor('array', 'list', ArrayEditor);

export default ArrayEditor;
